import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Loader } from 'lucide-react';

interface Option {
  value: number | string;
  label: string;
  sublabel?: string;
}

interface ServerSideSearchableDropdownProps {
  value: number | string | null;
  onChange: (value: number | string) => void;
  fetchOptions: (searchTerm: string) => Promise<Option[]>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  minSearchLength?: number;
  debounceMs?: number;
}

const ServerSideSearchableDropdown: React.FC<ServerSideSearchableDropdownProps> = ({
  value,
  onChange,
  fetchOptions,
  placeholder = 'Select an option...',
  disabled = false,
  className = '',
  label,
  required = false,
  minSearchLength = 2,
  debounceMs = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch initial selection if value exists
  useEffect(() => {
    if (value && !selectedOption) {
      // Fetch options with empty search to get the selected item
      fetchOptions('').then(opts => {
        const found = opts.find(opt => opt.value === value);
        if (found) {
          setSelectedOption(found);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedOption]);

  // Debounced search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchTerm.length >= minSearchLength || searchTerm.length === 0) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, debounceMs);
    } else {
      setOptions([]);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, minSearchLength, debounceMs]);

  const performSearch = async (term: string) => {
    setLoading(true);
    try {
      const results = await fetchOptions(term);
      setOptions(results);
    } catch (error) {
      console.error('Error fetching options:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
      // Load initial options when opened
      if (options.length === 0 && searchTerm.length === 0) {
        performSearch('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setSelectedOption(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSelectedOption(null);
    setSearchTerm('');
  };

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div ref={dropdownRef} className="relative">
        {/* Dropdown Trigger */}
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          className={`
            w-full flex items-center justify-between px-3 py-2 text-left border rounded-lg
            transition-colors duration-150
            ${disabled
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : 'bg-white hover:border-gray-400 cursor-pointer'
            }
            ${isOpen ? 'border-admin-600 ring-2 ring-admin-600 ring-opacity-20' : 'border-gray-300'}
          `}
        >
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
            {selectedOption ? (
              <div>
                <div>{selectedOption.label}</div>
                {selectedOption.sublabel && (
                  <div className="text-xs text-gray-500">{selectedOption.sublabel}</div>
                )}
              </div>
            ) : (
              placeholder
            )}
          </span>
          <div className="flex items-center gap-1">
            {selectedOption && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            )}
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
          </div>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={`Type at least ${minSearchLength} characters to search...`}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-admin-600 focus:border-transparent"
                />
                {loading && (
                  <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                )}
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  <Loader className="inline-block h-5 w-5 animate-spin mr-2" />
                  Loading...
                </div>
              ) : searchTerm.length > 0 && searchTerm.length < minSearchLength ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  Type at least {minSearchLength} characters to search
                </div>
              ) : options.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  No options found
                </div>
              ) : (
                options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`
                      w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors
                      ${option.value === value ? 'bg-admin-50 text-admin-700 font-medium' : 'text-gray-900'}
                    `}
                  >
                    <div>{option.label}</div>
                    {option.sublabel && (
                      <div className="text-xs text-gray-500">{option.sublabel}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerSideSearchableDropdown;
